import {
  createBinnedDataBufferFromData,
  GeckoBinnedDataBuffer,
} from 'features/dataLayers/geckoBinnedDataBuffer'
import {
  getGeneratedCdfParquetColumnName,
  getGeneratedCdfParquetFileName,
} from 'features/dataLayers/generation/generatedCdfParquet'
import { DuckDbInstanceContext } from 'features/duckDB'
import { useFileSignedUrlFetcher } from 'features/rolodex'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import { useCallback, useContext } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurement,
} from 'types'

import { useBinnedDataBufferCache } from './useBinnedDataBufferCache'

type DuckDbInstance = InstanceType<
  typeof import('@duckdb/duckdb-wasm').AsyncDuckDB
>

type Bin = {
  x: number
  y: number
  value: number
}

type BinSelection = {
  x_start: number
  x_end: number
  y_start: number
  y_end: number
}

// TODO: Where should these functions live?

function measureToXBinIndex(x: GeckoMeasurement, cdf: GeckoComponentDataFrame) {
  return Math.round(
    measurementRatio(
      x
        .minus(cdf.data.binning.bin_size.width.scale(0.5))
        .minus(cdf.data.geometry.x.start),
      cdf.data.binning.bin_size.width
    )
  )
}

function measureToYBinIndex(y: GeckoMeasurement, cdf: GeckoComponentDataFrame) {
  return Math.round(
    measurementRatio(
      y
        .minus(cdf.data.binning.bin_size.height.scale(0.5))
        .minus(cdf.data.geometry.y.start),
      cdf.data.binning.bin_size.height
    )
  )
}
function measurementRatio(a: GeckoMeasurement, b: GeckoMeasurement) {
  return a.num / b.convertTo(a.unit).num
}

export function gcrToBinSelection(
  rect: GeckoComponentRect | undefined,
  cdf: GeckoComponentDataFrame | undefined
): BinSelection | undefined {
  if (!rect || !cdf) {
    return
  }
  return rect && cdf
    ? {
        x_start: measureToXBinIndex(rect.start.x, cdf),
        x_end: measureToXBinIndex(rect.end.x, cdf),
        y_start: measureToYBinIndex(rect.start.y, cdf),
        y_end: measureToYBinIndex(rect.end.y, cdf),
      }
    : undefined
}

/**
 * A hook that returns a callback function that can be used to generate the data for
 * a given column in a CDS
 *
 * @returns     A function with two parameters, `cdf: GeckoComponentDataFrame` and `columnName: string`,
 *              that returns the data for the given `cdf` (as a `Promise<ReadonlyArray<number>>`)
 *              or `undefined` if it couldn't be fetched
 */
export function useCdfDataGenerator(): (
  cdf: GeckoComponentDataFrame,
  cropSelection?: GeckoComponentRect
) => Promise<GeckoBinnedDataBuffer | undefined> {
  const {
    getCachedBinnedDataBufferAccessor,
    deleteCachedBinnedDataBuffer,
    putBinnedDataBufferInCache,
  } = useBinnedDataBufferCache()
  const { fetchSignedUrl } = useFileSignedUrlFetcher()
  const { duckDbInstance, ensureDuckDbInstance } = useContext(
    DuckDbInstanceContext
  )

  /**
   * A function that generates a data grid a given CDS and column name, caching the result
   *
   * @param cdf       The CDF to fetch data for
   * @param columName The column in the parquet file to generate data from
   * @param cropSelection An optional x/y area to select data from.
   * @returns         The data for the CDF, or undefined if it couldn't be fetched
   */
  return useCallback(
    async (
      cdf: GeckoComponentDataFrame,
      cropSelection?: GeckoComponentRect
    ): Promise<GeckoBinnedDataBuffer | undefined> => {
      let db = duckDbInstance
      if (!db) {
        try {
          db = await ensureDuckDbInstance()
        } catch (error) {
          console.error('DuckDB instance not available', error)
          return createBinnedDataBufferFromData(new Float32Array([0]), cdf)
        }
      }
      if (!db) {
        return createBinnedDataBufferFromData(new Float32Array([0]), cdf)
      }

      const existingBinnedDataBuffer = getCachedBinnedDataBufferAccessor(
        cdf.id,
        undefined,
        cropSelection
      )

      // Custom/generated CDFs (no dispensed parquet column) are persisted
      // with their parquet file on the CDF entry itself, named
      // deterministically from the CDF id; the component-dataset tag on such
      // an entry only records which dataset family the layer belongs to.
      // Dispensed layers read their column from the parent
      // component-dataset's parquet.
      const isCustomCdf = !cdf.data._parquet_column_name
      const parquetEntryId = isCustomCdf
        ? cdf.id
        : (cdf.tags['component-dataset'] ?? '')
      const parquetFileName = isCustomCdf
        ? getGeneratedCdfParquetFileName(cdf.id)
        : (cdf.data.parquet_file_name ?? '')
      const parquetColumnName = isCustomCdf
        ? getGeneratedCdfParquetColumnName(cdf.id)
        : cdf.data._parquet_column_name

      if (existingBinnedDataBuffer?.generation === cdf.generation) {
        return await existingBinnedDataBuffer.retrieveBinnedDataBuffer()
      } else if (
        !isNil(existingBinnedDataBuffer) &&
        existingBinnedDataBuffer.generation !== cdf.generation
      ) {
        deleteCachedBinnedDataBuffer(cdf.id)
      }

      const valueGetterFn = () =>
        new Promise<GeckoBinnedDataBuffer>((resolve, reject) => {
          // get the signed URL
          fetchSignedUrl({
            queryOrQueries: {
              entry_id: parquetEntryId,
              path: parquetFileName,
            },
          }).then((signedUrl) => {
            // fetch the data from that signed URL
            if (!signedUrl?.url) {
              console.warn(
                'Failed to fetch signed URL for parquet file on entry: ',
                parquetEntryId
              )
              reject("Couldn't fetch signed URL")
              return
            }
            generateCdfData(
              db,
              signedUrl.url,
              cdf,
              cropSelection,
              parquetColumnName
            ).then((cdfData) => {
              if (cdfData && cdfData.binnedDataBuffer) {
                resolve(cdfData.binnedDataBuffer)
              } else {
                reject(new Error('CDF data undefined'))
              }
            })
          })
        })

      const memoizedValueGetterFn = memoize(valueGetterFn)

      putBinnedDataBufferInCache({
        id: cdf.id,
        generation: cdf.generation,
        data: memoizedValueGetterFn,
        cropSelection,
      })

      return await memoizedValueGetterFn()
    },
    [
      duckDbInstance,
      deleteCachedBinnedDataBuffer,
      ensureDuckDbInstance,
      fetchSignedUrl,
      getCachedBinnedDataBufferAccessor,
      putBinnedDataBufferInCache,
    ]
  )
}

/**
 * A function that pings a (rolodex signed) url to fetch a given column of data from a parquet
 * file and unpack it into a number array.
 *
 * @param url       The signed URL which points at the parquet file
 * @param column    The column in the parquet file to generate data from
 * @returns         The data for the CDF
 */
export const generateCdfData = async (
  db: DuckDbInstance,
  url: string,
  cdf: GeckoComponentDataFrame,
  cropSelection?: GeckoComponentRect,
  columnName: string | undefined = cdf.data._parquet_column_name
): Promise<
  { url: string; binnedDataBuffer: GeckoBinnedDataBuffer } | undefined
> => {
  try {
    if (!columnName) {
      throw Error('CDF parquet column name empty')
    }

    const binSelection = gcrToBinSelection(cropSelection, cdf)
    const bins = await queryBinsFromDuckDb(
      db,
      url,
      columnName,
      cdf.data.binning.x_bin_count,
      cdf.data.binning.y_bin_count,
      binSelection
    )
    const binnedData = generateDataArrayFromBins(
      bins,
      cdf.data.binning.x_bin_count,
      cdf.data.binning.y_bin_count,
      binSelection
    )

    const binnedDataBuffer = createBinnedDataBufferFromData(binnedData, cdf)
    return { url, binnedDataBuffer }
  } catch (error) {
    console.error(error)
  }
}

/**
 * A function that takes a list of bins and generates a data grid with each bin plotted.
 *
 * @param bins      A list of bins
 * @returns         A data grid with each bin value plotted in its respective index
 */
function generateDataArrayFromBins(
  bins: Array<Bin>,
  xBinCount: number,
  yBinCount: number,
  binSelection?: BinSelection
): Readonly<Float32Array> {
  if (bins.length === 0) {
    return new Float32Array()
  }

  const xLen = binSelection
    ? binSelection.x_end - binSelection.x_start
    : xBinCount
  const yLen = binSelection
    ? binSelection.y_end - binSelection.y_start
    : yBinCount

  const array = new Float32Array(xLen * yLen).fill(NaN)

  const xOffset = binSelection ? binSelection.x_start : 0
  const yOffset = binSelection ? binSelection.y_start : 0

  // DuckDB will sometimes return bin indices as BigInts, so we need to convert them to numbers
  // TODO: This is a hack and we should make a more robust solution
  for (let i = 0; i < bins.length; i++) {
    const bin = bins[i]
    const y_index = Number(bin.y) - yOffset
    const x_index = Number(bin.x) - xOffset

    array[y_index * xLen + x_index] = isNil(bin.value) ? NaN : bin.value
  }

  return array
}

async function queryBinsFromDuckDb(
  db: DuckDbInstance,
  url: string,
  column: string,
  xBinCount: number,
  yBinCount: number,
  binSelection: BinSelection | undefined
): Promise<Array<Bin>> {
  const conn = await db.connect()
  const dataGridResult = await conn.query(
    `SELECT x, y, "${column}" as value
     FROM '${url}'
     WHERE x >= ${binSelection?.x_start ?? 0}
       AND x
         < ${binSelection?.x_end ?? xBinCount}
       AND y >= ${binSelection?.y_start ?? 0}
       AND y
         < ${binSelection?.y_end ?? yBinCount}`
  )
  await conn.close()
  return dataGridResult.toArray()
}
